import { STARTUP_WARNING_CODES } from "./eventCodes";
import type { LiveInputEvent, LiveInputSession } from "./types";

/**
 * Строки журнала входа (спека 2026-10-24-stream-monitoring-ux, часть 2,
 * «Журнал событий»): одна строка на сессию подключения, серии коротких
 * переподключений схлопываются в одну строку «переподключался N раз».
 * Новые сверху: при живом эфире важнее последнее.
 */
export type EventLogRow =
  | {
      kind: "session";
      key: string;
      session: LiveInputSession;
      events: LiveInputEvent[];
    }
  | {
      kind: "reconnects";
      key: string;
      count: number;
      start: string;
      end: string | null;
      events: LiveInputEvent[];
    };

/** Хвост событий конца сессии (таймаут, затем отключение), как на бэкенде. */
const END_TAIL_MS = 30_000;
/** Окно стартовых предупреждений после подключения, как на бэкенде. */
const STARTUP_WINDOW_MS = 120_000;
/** Сессия короче минуты с паузой до следующей меньше двух минут это повтор. */
const SHORT_SESSION_MS = 60_000;
const SHORT_GAP_MS = 120_000;
/** Сколько коротких сессий подряд схлопываем в одну строку. */
const MIN_REPEAT_RUN = 3;

const time = (value: string) => new Date(value).getTime();

function isShort(session: LiveInputSession): boolean {
  return (
    session.end !== null &&
    session.end !== undefined &&
    time(session.end) - time(session.start) < SHORT_SESSION_MS
  );
}

/** События сессии по времени, старые первыми, без стартовых предупреждений. */
export function eventsInWindow(
  events: LiveInputEvent[],
  start: string,
  end: string | null | undefined,
  hideStartupWarnings: boolean
): LiveInputEvent[] {
  const from = time(start);
  const to = end ? time(end) + END_TAIL_MS : Number.POSITIVE_INFINITY;
  return events
    .filter((event) => {
      const at = time(event.at);
      if (at < from || at > to) return false;
      if (
        hideStartupWarnings &&
        STARTUP_WARNING_CODES.has(event.code.toUpperCase()) &&
        at - from <= STARTUP_WINDOW_MS
      ) {
        return false;
      }
      return true;
    })
    .sort((a, b) => time(a.at) - time(b.at));
}

export function buildEventLogRows(
  sessions: LiveInputSession[],
  events: LiveInputEvent[]
): EventLogRow[] {
  const sorted = [...sessions].sort((a, b) => time(a.start) - time(b.start));
  const rows: EventLogRow[] = [];
  let index = 0;

  while (index < sorted.length) {
    // Серия коротких сессий с короткими паузами между ними.
    let runEnd = index;
    while (
      runEnd + 1 < sorted.length &&
      isShort(sorted[runEnd]) &&
      time(sorted[runEnd + 1].start) - time(sorted[runEnd].end ?? "") <
        SHORT_GAP_MS
    ) {
      runEnd += 1;
    }
    const runLength = runEnd - index + 1;

    if (runLength >= MIN_REPEAT_RUN) {
      const first = sorted[index];
      const last = sorted[runEnd];
      rows.push({
        kind: "reconnects",
        key: `reconnects-${first.start}`,
        count: runLength,
        start: first.start,
        end: last.end ?? null,
        events: eventsInWindow(events, first.start, last.end, true),
      });
      index = runEnd + 1;
    } else {
      const session = sorted[index];
      rows.push({
        kind: "session",
        key: `session-${session.start}`,
        session,
        events: eventsInWindow(
          events,
          session.start,
          session.end,
          session.hadStartupWarnings
        ),
      });
      index += 1;
    }
  }

  return rows.reverse();
}
